<template>
    <section :data-mui-theme="theme" class="slice slice-list slice-list--with-background" aria-label="list" data-testid="list-with-background-slice">
        <div class="slice-content mui-background-lighter">
            <div class="slice-list--with-background__inner">
                <div class="slice-list--with-background__title">
                    <PrismicRichText
                        v-if="isFilled.richText(slice.primary.title)"
                        data-testid="list-with-background-title"
                        :field="slice.primary.title"
                        class="mui-title mui-title"
                    />
                    <VJoyButton
                        v-if="isFilled.link(slice.primary.cta)"
                        class="slice-list--with-background__cta"
                        :href="slice.primary.cta.url ? $withLocalePrefix(slice.primary.cta.url) : undefined"
                        variant="main"
                        :size="buttonSize"
                        data-testid="list-with-background-cta"
                    >
                        {{ slice.primary.cta.text }}
                    </VJoyButton>
                </div>

                <div class="slice-list--with-background__card" data-testid="list-with-background-card">
                    <div
                        v-for="(item, index) in slice.primary.list"
                        :key="index"
                        class="slice-list--with-background__item"
                        :data-testid="`list-with-background-item-${index}`"
                    >
                        <div class="slice-list--with-background__item-header">
                            <div
                                class="slice-list--with-background__item-marker mui-background-darker"
                                :class="{'slice-list--with-background__item-marker--icon': shouldShowIcon(item.icon)}"
                            >
                                <!-- TODO: Add icon back in
                              <VJoyIcon v-if="shouldShowIcon(item.icon)" :name="item.icon" size="small" /> -->
                                <span class="slice-list--with-background__item-index" :data-testid="`list-with-background-item-index-${index}`">
                                    {{ index + 1 }}
                                </span>
                            </div>
                            <h3
                                v-if="item.title"
                                class="slice-list--with-background__item-title mui-title"
                                :data-testid="`list-with-background-item-title-${index}`"
                            >
                                {{ item.title }}
                            </h3>
                        </div>

                        <PrismicRichText
                            v-if="isFilled.richText(item.description)"
                            :field="item.description"
                            class="slice-list--with-background__item-description"
                            :data-testid="`list-with-background-item-description-${index}`"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {ListSlice} from '@prismicio-types';
    import {useDisplayMobile} from '@malt/utils';
    import {VJoyButton} from '@maltjoy/core-vue';

    type ListWithBackgroundInZone = Extract<ListSlice, {variation: 'withBackground'}>;
    type ListWithBackgroundIcon = NonNullable<ListWithBackgroundInZone['primary']['list'][number]['icon']>;
    type ListWithBackgroundJoyIcon = Exclude<ListWithBackgroundIcon, 'pill'>;

    const props = defineProps(getSliceComponentProps<ListWithBackgroundInZone>());

    const {isDisplayMobile} = useDisplayMobile('Xs');
    const buttonSize = computed(() => (isDisplayMobile.value ? 'medium' : 'large'));

    function shouldShowIcon(icon: ListWithBackgroundIcon | null | undefined): icon is ListWithBackgroundJoyIcon {
        return isFilled.select(icon) && icon !== 'pill';
    }

    const theme = computed(() => props.slice.primary.theme);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-list--with-background {
        @include mq.screen_md(min) {
            padding: 0 var(--joy-core-spacing-6);
        }

        .slice-content {
            @include mq.screen_md(min) {
                margin: var(--joy-core-spacing-6) auto;
                border-radius: var(--joy-core-spacing-6);
            }
        }

        &__inner {
            text-align: left;

            @include mq.screen_sm(min) {
                display: flex;
                flex-direction: row;
                align-items: flex-start;
                gap: var(--joy-core-spacing-10);
            }
        }

        &__title {
            h2 {
                margin-bottom: var(--joy-core-spacing-8);
            }

            @include mq.screen_sm(min) {
                position: sticky;
                top: 95px;
                flex: 1;
                align-self: flex-start;
            }
        }

        &__cta {
            margin-bottom: var(--joy-core-spacing-8);

            @include mq.screen_sm(min) {
                margin-bottom: 0;
            }
        }

        &__card {
            display: flex;
            background-color: var(--joy-color-neutral-0);
            border-top-left-radius: var(--joy-core-radius-4);
            border-bottom-left-radius: var(--joy-core-radius-4);
            padding: var(--joy-core-spacing-8);
            border: 1px solid var(--joy-color-neutral-20);
            gap: var(--joy-core-spacing-8);
            overflow-x: scroll;
            cursor: grab;
            scrollbar-width: none;
            margin-right: calc(-1 * var(--joy-core-spacing-6));

            &::-webkit-scrollbar {
                display: none;
            }

            @include mq.screen_sm(min) {
                margin-right: 0;
                padding: 0;
                border-radius: var(--joy-core-radius-4);
                display: flex;
                flex-direction: column;
                width: auto;
                cursor: auto;
                gap: 0;
                flex: 1;
                overflow: visible;
            }

            @include mq.screen_lg(min) {
                min-width: 750px;
            }
        }

        &__item {
            @include mq.screen_sm(min) {
                padding: var(--joy-core-spacing-4) var(--joy-core-spacing-8);
            }
        }

        &__item-header {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            background-color: var(--joy-color-neutral-0);
        }

        &__item:not(:last-child) &__item-header {
            @include mq.screen_sm(min) {
                position: sticky;
                top: 10px;
            }
        }

        &__item:not(:last-child) {
            @include mq.screen_sm(min) {
                border-bottom: 1px solid var(--joy-color-neutral-20);
            }
        }

        &__item-marker {
            padding: var(--joy-core-spacing-3);
            margin-bottom: var(--joy-core-spacing-4);
            border-radius: 300px;
            color: var(--joy-color-neutral-0);

            @include mq.screen_sm(min) {
                padding: var(--joy-core-spacing-5);
            }
        }

        &__item-index {
            @include text.getFontBySize(secondary-200);
            position: relative;
            top: 5px;

            @include mq.screen_sm(min) {
                @include text.getFontBySize(secondary-600);
            }
        }

        &__item-title {
            padding-bottom: var(--joy-core-spacing-3);
        }

        &__item-description {
            @include text.getFontBySize(primary-300);
            letter-spacing: -0.2px;
            color: var(--joy-color-neutral-40);
            line-height: var(--joy-line-height-large);
            width: 200px;

            @include mq.screen_sm(min) {
                @include text.getFontBySize(primary-600);
                padding-bottom: var(--joy-core-spacing-4);
                width: 100%;
            }
        }
    }
</style>
